import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircleIcon,
  BuildingOfficeIcon,
  ArrowDownTrayIcon,
  ArrowRightIcon,
  ChartBarIcon,
  BanknotesIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import CompanyLogo from '../../common/CompanyLogo';

interface FinalConfirmationProps {
  finalSelection: any;
  onUpdateState: (updates: any) => void;
}

const FinalConfirmation: React.FC<FinalConfirmationProps> = ({ finalSelection, onUpdateState }) => {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  // Debug log
  console.log('FinalConfirmation - finalSelection:', finalSelection);

  // Error handling for missing finalSelection
  if (!finalSelection) {
    return (
      <div className="min-h-screen bg-gradient-financial py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-red-600">Error: No selection data available. Please go back and complete the selection process.</p>
          <button 
            onClick={() => navigate(-1)}
            className="mt-4 text-primary-600 hover:text-primary-500"
          >
            ← Go Back
          </button>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(Math.round(num));
  };

  const handleConfirm = async () => {
    console.log('=== FINAL CONFIRMATION HANDLE CONFIRM ===');
    console.log('finalSelection:', finalSelection);
    console.log('eligibilityCheckId:', finalSelection?.eligibilityCheckId);
    
    setIsProcessing(true);
    try {
      // Get user data from localStorage
      const userData = localStorage.getItem('user');
      const user = userData ? JSON.parse(userData) : null;
      const userEmail = user?.email || 'test@example.com';
      
      console.log('User email:', userEmail);
      
      // Validate required fields
      if (!finalSelection.eligibilityCheckId) {
        throw new Error('Missing eligibility check ID. Please restart the process.');
      }
      
      // Always use the main endpoint - it no longer requires authentication
      const endpoint = '/api/user-selections';
      
      const response = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${endpoint}`, {
        ticker: finalSelection.ticker,
        companyName: finalSelection.companyName,
        selectedAmount: finalSelection.commitmentAmount,
        sharePriceAtSelection: finalSelection.sharePriceAtSelection,
        discountRate: (finalSelection.discountRate || 0) / 100, // Convert percentage to decimal
        vwapPercent: finalSelection.vwapPercent || 0.1, // Use from finalSelection or default
        rapidPercent: finalSelection.rapidPercent || 0.2, // Use from finalSelection or default
        applyingFor: finalSelection.applyingFor || 'self',
        originationFeeRate: typeof finalSelection.originationFeeRate === 'number' ? finalSelection.originationFeeRate : 0,
        userEmail: userEmail,
        includeAdvance: finalSelection.includeAdvance || false,
        advanceAmount: finalSelection.advanceAmount || 0,
        commitmentShares: finalSelection.commitmentShares || 0,
        eligibilityCheckId: finalSelection.eligibilityCheckId,
        registrationRequirement: finalSelection.registrationRequirement || '30 calendar days'
      });

      if (response.data.success) {
        onUpdateState({ facilityId: response.data.data.facilityId });
        // Navigate directly to questionnaire with facility ID
        navigate(`/questionnaire?facilityId=${response.data.data.facilityId}`);
      }
    } catch (error: any) {
      console.error('Error confirming selection:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // More specific error messages
      if (error.response?.status === 400) {
        alert(error.response.data.message || 'Invalid request. Please check your data and try again.');
      } else if (error.response?.status === 404) {
        alert(error.response.data.message || 'Resource not found. Please start the process over.');
      } else if (error.response?.status === 500) {
        alert(error.response.data.message || 'Server error. Please try again later.');
      } else {
        alert(`Failed to process your selection: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadTermSheet = async () => {
    setIsDownloading(true);
    try {
      console.log('=== FINAL CONFIRMATION TERM SHEET DOWNLOAD START ===');
      console.log('Final selection data:', finalSelection);
      
      // Get user data from localStorage
      const userData = localStorage.getItem('user');
      const user = userData ? JSON.parse(userData) : null;
      const userEmail = user?.email || 'test@example.com';
      
      // Always use the main endpoint - authentication is not required for initial flow
      const endpoint = '/api/user-selections';
      
      const selectionResponse = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${endpoint}`, {
        ticker: finalSelection.ticker,
        companyName: finalSelection.companyName,
        selectedAmount: finalSelection.commitmentAmount,
        sharePriceAtSelection: finalSelection.sharePriceAtSelection,
        discountRate: (finalSelection.discountRate || 0) / 100,
        vwapPercent: finalSelection.vwapPercent || 0.1,
        rapidPercent: finalSelection.rapidPercent || 0.2,
        applyingFor: finalSelection.applyingFor || 'self',
        originationFeeRate: typeof finalSelection.originationFeeRate === 'number' ? finalSelection.originationFeeRate : 0,
        userEmail: userEmail,
        includeAdvance: finalSelection.includeAdvance || false,
        advanceAmount: finalSelection.advanceAmount || 0,
        commitmentShares: finalSelection.commitmentShares || 0,
        eligibilityCheckId: finalSelection.eligibilityCheckId
      });

      console.log('Selection response:', selectionResponse.data);

      if (selectionResponse.data.success) {
        const selectionId = selectionResponse.data.data.selectionId;
        console.log('Created selectionId:', selectionId);
        
        // Directly download DOCX
        console.log('Downloading document...');
        // Use the main download endpoint
        const downloadEndpoint = `/api/document/download/${selectionId}?format=docx`;
        
        const downloadResponse = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${downloadEndpoint}`,
          { 
            responseType: 'blob',
            timeout: 60000
          }
        );
        
        console.log('Download response:', {
          status: downloadResponse.status,
          contentType: downloadResponse.headers['content-type'],
          size: downloadResponse.data.length
        });
        
        if (!downloadResponse.data || downloadResponse.data.length === 0) {
          throw new Error('Received empty response from server');
        }
        
        const blob = new Blob([downloadResponse.data], { 
          type: downloadResponse.headers['content-type'] || 'application/octet-stream' 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `TermSheet-${selectionId}.docx`);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
        
        console.log('✅ Term sheet downloaded successfully');
      } else {
        throw new Error('Failed to create user selection');
      }
    } catch (error: any) {
      console.error('=== FINAL CONFIRMATION TERM SHEET DOWNLOAD ERROR ===');
      console.error('Error downloading term sheet:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      
      if (error.response?.status === 404) {
        alert('Selection not found. Please ensure you have completed the deal options step and try again.');
      } else if (error.response?.status === 500) {
        alert('Server error occurred while generating the term sheet. Please try again later.');
      } else if (error.response?.data?.error) {
        alert(`Download failed: ${error.response.data.error}`);
      } else if (error.code === 'ECONNABORTED') {
        alert('Download timed out. Please try again.');
      } else {
        alert(`Failed to download term sheet: ${error.message}`);
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleViewTermSheet = async () => {
    try {
      console.log('=== VIEW TERM SHEET START ===');
      console.log('Final selection data:', finalSelection);
      
      // Get user data from localStorage
      const userData = localStorage.getItem('user');
      const user = userData ? JSON.parse(userData) : null;
      const userEmail = user?.email || 'test@example.com';
      
      // Always use the main endpoint - authentication is not required for initial flow
      const endpoint = '/api/user-selections';
      
      const selectionResponse = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${endpoint}`, {
        ticker: finalSelection.ticker,
        companyName: finalSelection.companyName,
        selectedAmount: finalSelection.commitmentAmount,
        sharePriceAtSelection: finalSelection.sharePriceAtSelection,
        discountRate: (finalSelection.discountRate || 0) / 100,
        vwapPercent: finalSelection.vwapPercent || 0.1,
        rapidPercent: finalSelection.rapidPercent || 0.2,
        applyingFor: finalSelection.applyingFor || 'self',
        originationFeeRate: typeof finalSelection.originationFeeRate === 'number' ? finalSelection.originationFeeRate : 0,
        userEmail: userEmail,
        includeAdvance: finalSelection.includeAdvance || false,
        advanceAmount: finalSelection.advanceAmount || 0,
        commitmentShares: finalSelection.commitmentShares || 0,
        eligibilityCheckId: finalSelection.eligibilityCheckId
      });

      if (selectionResponse.data.success) {
        const selectionId = selectionResponse.data.data.selectionId;
        const facilityId = selectionResponse.data.data.facilityId;
        console.log('Created selectionId for viewing:', selectionId);
        console.log('FacilityId for viewing:', facilityId);
        
        // Store facilityId in localStorage for term sheet viewer
        const userData = localStorage.getItem('user');
        if (userData) {
          const user = JSON.parse(userData);
          user.facilityId = facilityId;
          localStorage.setItem('user', JSON.stringify(user));
        }
        
        // Navigate to term sheet viewer with selectionId
        navigate(`/term-sheet/${selectionId}`);
      } else {
        throw new Error('Failed to create user selection for viewing');
      }
    } catch (error: any) {
      console.error('=== VIEW TERM SHEET ERROR ===');
      console.error('Error creating selection for viewing:', error);
      alert(`Failed to view term sheet: ${error.message}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-financial py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Success Header */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 bg-success-100 rounded-full mb-6">
            <RocketLaunchIcon className="h-12 w-12 text-success-600" />
          </div>
          <h1 className="text-4xl font-bold text-success-600 mb-4">
            Congratulations!
          </h1>
          <p className="text-2xl text-secondary-900 mb-2">
            Your Equity Facility is Almost Ready
          </p>
          <p className="text-lg text-secondary-600">
            Review your selected terms below and proceed to complete your company profile
          </p>
        </motion.div>

        {/* Company & Facility Summary */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-elevated mb-8"
        >
          <div className="flex items-center gap-4 mb-6">
            <CompanyLogo 
              ticker={finalSelection?.ticker}
              className="w-12 h-12 rounded-xl"
            />
            <div>
              <h2 className="text-2xl font-bold text-secondary-900">
                {finalSelection?.companyName} ({finalSelection?.ticker})
              </h2>
              <p className="text-secondary-600">Equity Finance Facility Agreement</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Facility Details */}
            <div>
              <h3 className="font-semibold text-secondary-900 mb-4">Facility Details</h3>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-secondary-100">
                  <span className="text-secondary-600">Total Facility Size</span>
                  <span className="font-semibold text-secondary-900">
                    {formatCurrency(finalSelection?.commitmentAmount || 0)}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-secondary-100">
                  <span className="text-secondary-600">Discount Rate</span>
                  <span className="font-semibold text-secondary-900">
                    {finalSelection?.discountRate || 0}%
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-secondary-100">
                  <span className="text-secondary-600">Origination Fee</span>
                  <span className="font-semibold text-secondary-900">
                    {finalSelection?.applyingFor === 'other'
                      ? `${(((finalSelection?.originationFeeRate ?? 0) as number) * 100).toFixed(2)}%`
                      : 'None'}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-secondary-100">
                  <span className="text-secondary-600">Share Price at Agreement</span>
                  <span className="font-semibold text-secondary-900">
                    ${finalSelection?.sharePriceAtSelection?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-secondary-600">Facility Duration</span>
                  <span className="font-semibold text-secondary-900">24 Months</span>
                </div>
              </div>
            </div>

            {/* Advance Draw Details */}
            <div>
              <h3 className="font-semibold text-secondary-900 mb-4">Advance Draw Details</h3>
              {finalSelection?.includeAdvance ? (
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-secondary-100">
                    <span className="text-secondary-600">Advance Amount</span>
                    <span className="font-semibold text-secondary-900">
                      {formatCurrency(finalSelection?.advanceAmount || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-secondary-100">
                    <span className="text-secondary-600">Commitment Shares</span>
                    <span className="font-semibold text-secondary-900">
                      {formatNumber(finalSelection?.commitmentShares || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-secondary-100">
                    <span className="text-secondary-600">Effective Price per Share</span>
                    <span className="font-semibold text-secondary-900">
                      ${((finalSelection.sharePriceAtSelection || 0) * (1 - (finalSelection.discountRate || 0) / 100)).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-secondary-600">Remaining Facility</span>
                    <span className="font-semibold text-secondary-900">
                      {formatCurrency((finalSelection.commitmentAmount || 0) - (finalSelection.advanceAmount || 0))}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="bg-secondary-50 rounded-xl p-4">
                  <p className="text-secondary-600">No advance draw selected</p>
                  <p className="text-sm text-secondary-500 mt-1">
                    Full facility amount remains available for future exercises
                  </p>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Key Terms */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card mb-8"
        >
          <h3 className="text-xl font-semibold text-secondary-900 mb-4">Key Terms & Advantages</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 flex-shrink-0">
                <BanknotesIcon className="h-full w-full text-primary-600" />
              </div>
              <div>
                <p className="font-medium text-secondary-900">Access Instant Capital</p>
                <p className="text-sm text-secondary-600">Advance Draw available within 24 hours of executing final deal docs</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 flex-shrink-0">
                <ChartBarIcon className="h-full w-full text-primary-600" />
              </div>
              <div>
                <p className="font-medium text-secondary-900">Proprietary Technology Advantage</p>
                <p className="text-sm text-secondary-600">Dashboard access for fully automated tracking and management</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 flex-shrink-0">
                <RocketLaunchIcon className="h-full w-full text-primary-600" />
              </div>
              <div>
                <p className="font-medium text-secondary-900">Unmatched Speed and Flexibility</p>
                <p className="text-sm text-secondary-600">VWAP and rapid exercise options, competitive terms, and a completely streamlined process for real-time monitoring with predictable settlement schedules.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 flex-shrink-0">
                <BanknotesIcon className="h-full w-full text-primary-600" />
              </div>
              <div>
                <p className="font-medium text-secondary-900">Purchase Settlements</p>
                <p className="text-sm text-secondary-600">Following the conclusion of the Valuation Period, settlement will be effected within one (1) hour, by means of wire or real-time ACH transfer</p>
              </div>
            </div>
          </div>
          <div className="text-center mt-6">
            <p className="text-base font-semibold text-primary-700 italic">
              Make gametime decisions with precision accuracy
            </p>
          </div>
        </motion.div>

        {/* Terms Acceptance */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card border-primary-200 bg-primary-50 mb-8"
        >
          <div className="space-y-4">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="terms"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-1 h-4 w-4 text-primary-950 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="terms" className="text-sm text-primary-700">
                I agree to the Terms and Conditions and Privacy Policy of this website.
              </label>
            </div>
            
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="disclaimer"
                checked={disclaimerAccepted}
                onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                className="mt-1 h-4 w-4 text-primary-950 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="disclaimer" className="text-sm text-primary-700">
                I understand that Twenty Four Capital is not registered with the SEC, FINRA or any other Financial Regulator. 
                As such, none of the information available on our website should be utilized for investment decisions. 
                Use of this platform is not to be construed as a solicitation for buying or selling securities.
            </label>
            </div>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-4">
          <button
            onClick={handleConfirm}
            disabled={!termsAccepted || !disclaimerAccepted || isProcessing}
            className="btn-primary w-full flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? 'Processing...' : 'Confirm & Proceed'}
            <ArrowRightIcon className="h-5 w-5 ml-2" />
          </button>
          <div className="flex flex-col sm:flex-row gap-4">
            <button 
              onClick={handleViewTermSheet}
              className="btn-secondary flex-1 flex items-center justify-center"
            >
              <ChartBarIcon className="h-5 w-5 mr-2" />
              View Term Sheet
            </button>
            <button 
              onClick={handleDownloadTermSheet}
              disabled={isDownloading}
              className="btn-secondary flex-1 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {!isDownloading && <ArrowDownTrayIcon className="h-5 w-5 mr-2" />}
              {isDownloading ? 'Downloading...' : 'Download Term Sheet'}
            </button>
          </div>
        </div>

        {/* Back Button */}
        <div className="text-center mt-4">
          <button 
            onClick={() => navigate(-1)}
            className="text-secondary-600 hover:text-secondary-800 font-medium"
          >
            ← Back to Advance Options
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinalConfirmation; 
